import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const Pricing = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      const { data } = await supabase.from('plans').select('*').eq('is_active', true).order('price');
      if (data) setPlans(data);
      setLoading(false);
    };
    fetchPlans();
  }, []);

  const growthPlan = plans.find(p => p.slug === 'growth');
  const ultimatePlan = plans.find(p => p.slug === 'ultimate');

  const getPrice = (plan) => {
    if (!plan) return '—';
    if (billingCycle === 'yearly') return Math.round(plan.price * 0.83); // ~17% discount
    return plan.price;
  };

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">

      {/* HEADER */}
      <div className="bg-gray-50 py-20 px-4 text-center">
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">
          Plans that scale with you
        </h1>
        <p className="text-gray-500 text-lg max-w-2xl mx-auto mb-8">
          Start for free, upgrade as you grow. No hidden fees.
        </p>

        {/* TOGGLE */}
        <div className="flex items-center justify-center gap-4">
          <span className={`text-sm font-bold ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
          <button
            onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
            className="w-14 h-8 bg-gray-900 rounded-full relative transition-colors focus:outline-none"
          >
            <div className={`w-6 h-6 bg-white rounded-full absolute top-1 transition-transform ${billingCycle === 'monthly' ? 'left-1' : 'translate-x-7 left-1'}`}></div>
          </button>
          <span className={`text-sm font-bold ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
            Yearly <span className="text-[#ee5b2b] text-xs ml-1">(Save ~20%)</span>
          </span>
        </div>
      </div>

      {/* LOADING STATE */}
      {loading ? (
        <div className="py-20 text-center text-gray-400 font-bold">Loading plans...</div>
      ) : (

        /* CARDS CONTAINER */
        <div className="max-w-7xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-3 gap-8 -mt-10">

          {/* === 1. STARTER (FREE) === */}
          <div className="bg-white p-8 rounded-3xl border border-gray-200 shadow-lg flex flex-col relative overflow-hidden">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-500 uppercase tracking-wider mb-2">Starter</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-gray-900">₹0</span>
                <span className="text-gray-400 font-medium text-sm">/ forever</span>
              </div>
              <p className="text-gray-500 mt-4 text-sm leading-relaxed">
                For testing the waters.
              </p>
            </div>

            <button onClick={() => navigate('/dashboard')} className="w-full py-3 border-2 border-gray-200 text-gray-900 font-bold rounded-xl hover:border-gray-900 hover:bg-gray-50 transition-all mb-8">
              Get Started Free
            </button>

            <div className="space-y-4 flex-1">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Limits</p>
              <ul className="space-y-3">
                <li className="flex gap-3 text-sm font-medium text-gray-700">
                  <span className="text-gray-400 font-bold">3</span>
                  <span>Menu Items Only</span>
                </li>
                <li className="flex gap-3 text-sm font-medium text-gray-700">
                  <span className="text-gray-400 font-bold">100</span>
                  <span>AR Views / month</span>
                </li>
              </ul>

              <div className="border-t border-gray-100 my-4"></div>

              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Features</p>
              <ul className="space-y-3">
                <li className="flex gap-3 text-sm font-medium text-gray-700"><span className="text-green-500 font-bold">✓</span> General QR Code</li>
                <li className="flex gap-3 text-sm font-medium text-gray-700"><span className="text-green-500 font-bold">✓</span> Basic Revenue Reports</li>
                <li className="flex gap-3 text-sm font-medium text-gray-400"><span className="text-gray-300">✕</span> No Live Ordering</li>
                <li className="flex gap-3 text-sm font-medium text-gray-400"><span className="text-gray-300">✕</span> Standard Branding</li>
              </ul>
            </div>
          </div>

          {/* === 2. GROWTH === */}
          <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col relative overflow-hidden border-2 border-[#ee5b2b] transform md:-translate-y-4">
            <div className="absolute top-0 right-0 bg-[#ee5b2b] text-white text-xs font-bold px-4 py-1 rounded-bl-xl">
              MOST POPULAR
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-[#ee5b2b] uppercase tracking-wider mb-2">{growthPlan?.name || 'Growth'}</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-5xl font-extrabold text-gray-900">₹{getPrice(growthPlan)}</span>
                <span className="text-gray-400 font-medium">/ month</span>
              </div>
              <p className="text-gray-500 mt-4 text-sm leading-relaxed">
                Perfect for small cafes & restaurants.
              </p>
            </div>

            <button onClick={() => navigate('/dashboard')} className="w-full py-4 bg-[#ee5b2b] text-white font-bold rounded-xl shadow-lg hover:bg-[#d94a1a] transition-all mb-8">
              Choose {growthPlan?.name || 'Growth'}
            </button>

            <div className="space-y-4 flex-1">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Increased Limits</p>
              <ul className="space-y-3">
                {(growthPlan?.features || ['30 Menu Items', '5000 AR Views/mo', 'Live Ordering', 'Custom Branding', 'Analytics', 'CSV Export']).map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm font-medium text-gray-900">
                    <span className="text-green-500 font-bold">✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* === 3. ULTIMATE === */}
          <div className="bg-gray-900 p-8 rounded-3xl border border-gray-800 shadow-xl flex flex-col relative overflow-hidden text-white">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-400 uppercase tracking-wider mb-2">{ultimatePlan?.name || 'Ultimate'}</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-white">₹{getPrice(ultimatePlan)}</span>
                <span className="text-gray-400 font-medium text-sm">/ month</span>
              </div>
              <p className="text-gray-400 mt-4 text-sm leading-relaxed">
                No limits. For serious businesses.
              </p>
            </div>

            <button onClick={() => navigate('/dashboard')} className="w-full py-3 bg-white text-black font-bold rounded-xl hover:bg-gray-200 transition-all mb-8">
              Go Unlimited 🚀
            </button>

            <div className="space-y-4 flex-1">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">All Growth features, plus:</p>
              <ul className="space-y-3">
                {(ultimatePlan?.features || ['Unlimited Items', 'Unlimited AR Views', 'Smart Upselling', 'Customer Ratings', 'Access to latest feature', 'Custom feature requestable']).map((f, i) => (
                  <li key={i} className="flex gap-3 text-sm font-medium text-white">
                    <span className="text-green-400 font-bold">✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>
      )}

      {/* FOOTER AREA */}
      <div className="text-center pb-20 px-4">
        <h3 className="font-bold text-lg mb-2">Need a Custom Enterprise Plan?</h3>
        <p className="text-gray-500 mb-6">For chains with more than 10 locations.</p>
        <a href="https://mail.google.com/mail/?view=cm&fs=1&to=pator.explore.tech@gmail.com&su=Custom%20Enterprise%20Plan" target="_blank" rel="noopener noreferrer" className="text-[#ee5b2b] font-bold border-b-2 border-[#ee5b2b] hover:text-black hover:border-black transition-colors">
          Contact Sales
        </a>
      </div>

    </div>
  );
};

export default Pricing;